import { Link } from '@inertiajs/react'
import { ReactNode, useRef, useEffect } from 'react'

interface Animation {
  id: number
  name: string
  description?: string
}

interface Props {
  animation: Animation
  allAnimations: { id: number; name: string }[]
  children: ReactNode
}

export default function AnimationLayout({ animation, allAnimations, children }: Props) {
  const currentIndex = allAnimations.findIndex(a => a.id === animation.id)
  const prevAnimation = currentIndex > 0 ? allAnimations[currentIndex - 1] : null
  const nextAnimation = currentIndex < allAnimations.length - 1 ? allAnimations[currentIndex + 1] : null
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const currentButtonRef = useRef<HTMLAnchorElement>(null)

  // Scroll to current animation button on mount (mobile only)
  useEffect(() => {
    if (currentButtonRef.current && scrollContainerRef.current) {
      // Only scroll on mobile (when container has overflow)
      const container = scrollContainerRef.current
      if (container.scrollWidth > container.clientWidth) {
        if (currentIndex <= 2) {
          container.scrollLeft = 0
        } else {
          currentButtonRef.current.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'center'
          })
        }
      }
    }
  }, [animation.id, currentIndex])

  return (
    <div className="min-h-screen bg-tessan-green relative overflow-hidden">
      {/* Header - responsive */}
      <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 z-50">
        {/* Mobile: stacked layout */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
          {/* Row 1 on mobile: Back + Replay */}
          <div className="flex justify-between items-center sm:contents">
            <Link
              href="/animations"
              className="text-tessan-cream/70 hover:text-tessan-cream flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden sm:inline">Retour à la liste</span>
              <span className="sm:hidden">Retour</span>
            </Link>

            {/* Title - hidden on mobile here, shown below */}
            <div className="hidden sm:block text-white text-center">
              <span className="text-tessan-yellow font-bold">#{animation.id}</span>
              <span className="mx-2">—</span>
              <span>{animation.name}</span>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="bg-tessan-yellow text-tessan-green px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 hover:bg-tessan-yellow-cta font-semibold text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="hidden sm:inline">Rejouer</span>
            </button>
          </div>

          {/* Title - visible only on mobile */}
          <div className="sm:hidden text-white text-center text-sm">
            <span className="text-tessan-yellow font-bold">#{animation.id}</span>
            <span className="mx-1">—</span>
            <span>{animation.name}</span>
          </div>
        </div>
      </div>

      {/* Content - adjusted padding for mobile */}
      <div className="pt-24 sm:pt-20 pb-24">
        {children}
      </div>

      {/* Bottom navigation bar - prev/next + thumbnails */}
      <div className="fixed bottom-0 left-0 right-0 bg-tessan-green/95 backdrop-blur-sm py-3 sm:py-4 z-50 border-t border-tessan-cream/10">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4">
          {/* Previous button */}
          {prevAnimation ? (
            <Link
              href={`/animations/${prevAnimation.id}`}
              className="flex-shrink-0 bg-tessan-cream/10 hover:bg-tessan-cream/20 text-white p-2 sm:px-3 sm:py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden lg:inline text-sm">{prevAnimation.name}</span>
            </Link>
          ) : <div className="w-10 sm:w-12" />}

          {/* Thumbnail navigation - scroll on mobile, wrap on desktop */}
          <div
            ref={scrollContainerRef}
            className="flex-1 flex gap-1.5 sm:gap-2 overflow-x-auto sm:overflow-visible scrollbar-hide justify-start sm:justify-center sm:flex-wrap"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {allAnimations.map((a) => (
              <Link
                key={a.id}
                ref={a.id === animation.id ? currentButtonRef : null}
                href={`/animations/${a.id}`}
                className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  a.id === animation.id
                    ? 'bg-tessan-yellow text-tessan-green scale-110'
                    : 'bg-tessan-cream/20 text-white/70 hover:bg-tessan-cream/40'
                }`}
              >
                {a.id}
              </Link>
            ))}
          </div>

          {/* Next button */}
          {nextAnimation ? (
            <Link
              href={`/animations/${nextAnimation.id}`}
              className="flex-shrink-0 bg-tessan-cream/10 hover:bg-tessan-cream/20 text-white p-2 sm:px-3 sm:py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <span className="hidden lg:inline text-sm">{nextAnimation.name}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          ) : <div className="w-10 sm:w-12" />}
        </div>
      </div>
    </div>
  )
}
